import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Menu, 
  X, 
  Home, 
  BarChart3, 
  LogOut,
  User,
  Link as LinkIcon,
  ScanSearch,
  Megaphone,
  Sparkles
} from 'lucide-react';

const brandLogoSrc = `${process.env.PUBLIC_URL}/brand/affili8-marketing-logo.png`;

const SidebarBrand = () => (
  <div className="flex-shrink-0 px-4">
    <img
      src={brandLogoSrc}
      alt="Affili8 marketing"
      className="h-11 w-auto max-w-[220px] object-contain object-left mx-auto lg:mx-0"
    />
    <h1 className="mt-5 text-xl font-semibold text-gray-900 text-center lg:text-left">
      Affili8 Monitor
    </h1>
  </div>
);

const Layout = ({ children }) => {
  const { user, logout, canAccessAdmin, canAccessLinkIntelligence } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  const navigation = [
    { name: 'Content', href: '/dashboard', icon: Home },
    { name: 'Link Building', href: '/link-building', icon: LinkIcon },
    { name: 'Digital PR', href: '/digital-pr', icon: Megaphone },
    ...(canAccessAdmin
      ? [{ name: 'AI LLM Mentions', href: '/ai-llm-mentions', icon: Sparkles }]
      : []),
    ...(canAccessLinkIntelligence
      ? [{ name: 'Link Intelligence', href: '/link-intelligence', icon: ScanSearch }]
      : []),
    ...(canAccessAdmin
      ? [
          { name: 'Admin Panel', href: '/admin', icon: BarChart3 }
        ]
      : [])
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white">
          <div className="absolute top-0 right-0 -mr-12 pt-2">
            <button
              type="button"
              className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-6 w-6 text-white" />
            </button>
          </div>
          <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
            <SidebarBrand />
            <nav className="mt-5 px-2 space-y-1">
              {navigation.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`group flex items-center px-2 py-2 text-base font-medium rounded-md ${
                      location.pathname === item.href
                        ? 'bg-primary-100 text-primary-900'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <Icon className="mr-4 h-6 w-6" />
                    {item.name}
                  </Link>
                );
              })}
            </nav>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:flex lg:w-64 lg:flex-col lg:fixed lg:inset-y-0">
        <div className="flex-1 flex flex-col min-h-0 bg-white border-r border-gray-200">
          <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
            <SidebarBrand />
            <nav className="mt-5 flex-1 px-2 space-y-1">
              {navigation.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                      location.pathname === item.href
                        ? 'bg-primary-100 text-primary-900'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <Icon className="mr-3 h-5 w-5" />
                    {item.name}
                  </Link>
                );
              })}
            </nav>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64 flex flex-col flex-1">
        {/* Top bar */}
        <div className="sticky top-0 z-10 flex-shrink-0 flex h-16 bg-white shadow">
          <button
            type="button"
            className="px-4 border-r border-gray-200 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </button>
          <div className="flex-1 px-4 flex justify-between">
            <div className="flex-1 flex">
              <h1 className="text-2xl font-semibold text-gray-900 flex items-center">
                {location.pathname === '/dashboard' && 'Content'}
                {location.pathname === '/admin' && 'Admin Panel'}
                {location.pathname === '/link-building' && 'Link Building'}
                {location.pathname === '/digital-pr' && 'Digital PR'}
                {location.pathname === '/ai-llm-mentions' && 'AI LLM Mentions'}
                {location.pathname.startsWith('/link-intelligence') && 'Link Intelligence'}
              </h1>
            </div>
            <div className="ml-4 flex items-center md:ml-6">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <User className="h-5 w-5 text-gray-400" />
                  <span className="text-sm font-medium text-gray-700">{user?.username}</span>
                </div>
                <button
                  onClick={logout}
                  className="btn btn-secondary flex items-center"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
              {children}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout; 